import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Dropdown,
  Icon,
  Row,
  Table,
  useToggle,
} from '@met/react-components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ConfirmationModal, LinkButton, PageHeader } from '../../components';
import { buildsActions, versionsActions } from '../../store';
import AddVersionModal from './components/AddVersionModal';

const Publish = props => {
  const dispatch = useDispatch();
  const [selectedSourceVersion, setSelectedSourceVersion] = useState(null);
  const [selectedTargetVersion, setSelectedTargetVersion] = useState(null);
  const projectName = useSelector(state => state.versions.projectName);
  const versions = useSelector(state => state.versions.versions);
  const isPublishingLatest = useSelector(
    state => state.builds.publishLatest.isLoading
  );
  const [addVersionModalHidden, toggleVersionModal] = useToggle(true);
  const [deleteVersionModalHidden, toggleDeleteVersionModal] = useToggle(true);
  const [versionToDelete, setVersionToDelete] = useState(null);
  const [copyVersionModalHidden, toggleCopyVersionModal] = useToggle(true);

  useEffect(() => {
    dispatch(versionsActions.getVersions(props.match.params.id));
  }, [dispatch, props.match.params]);

  const handlePublishLatestClick = useCallback(() => {
    dispatch(buildsActions.publishLatest(props.match.params.id))
      .then(() => toast.success('Latest version successfully published!'))
      .catch(() =>
        toast.error(
          'An error occurred while publishing the Latest version...Please try again later.'
        )
      )
      .then(() => dispatch(versionsActions.getVersions(props.match.params.id)));
  }, [dispatch, props.match.params]);

  const handleAddVersionSave = useCallback(
    versionName => {
      return dispatch(
        versionsActions.addVersion(props.match.params.id, versionName)
      ).then(() =>
        dispatch(versionsActions.getVersions(props.match.params.id))
      );
    },
    [dispatch, props.match.params]
  );

  const handleDeleteVersion = versionId => {
    setVersionToDelete(versionId);
    toggleDeleteVersionModal();
  };

  const deleteVersion = useCallback(() => {
    dispatch(versionsActions.deleteVersion(versionToDelete))
      .then(() => toggleDeleteVersionModal())
      .then(() => dispatch(versionsActions.getVersions(props.match.params.id)));
  }, [dispatch, props.match.params, versionToDelete, toggleDeleteVersionModal]);

  const handleCopyVersion = useCallback(() => {
    dispatch(
      buildsActions.copy(selectedSourceVersion.id, selectedTargetVersion.id)
    )
      .then(() => toggleCopyVersionModal())
      .then(() => toast.success('Version successfully copied!'))
      .then(() => dispatch(versionsActions.getVersions(props.match.params.id)));
  }, [
    dispatch,
    selectedSourceVersion,
    selectedTargetVersion,
    toggleCopyVersionModal,
    props.match.params,
  ]);

  const cols = {
    name: {
      label: 'Name',
    },
    lastPublishedAt: {
      label: 'Last Published At',
      content: version =>
        version.lastPublishedAt ? version.lastPublishedAt : 'Never',
    },
    actions: {
      label: 'Actions',
      content: version =>
        version.name === 'Latest' ? null : (
          <LinkButton onClick={() => handleDeleteVersion(version.id)}>
            <Icon type='trash' />
          </LinkButton>
        ),
    },
  };

  return (
    <div>
      <PageHeader
        title={
          <Link to={`/projects/${props.match.params.id}`}>{projectName}</Link>
        }
        subtitle='Publish Translations'
        actions={[
          {
            label: 'Add Version',
            icon: 'code-branch',
            onClick: toggleVersionModal,
          },
        ]}
      />
      <Row>
        <Col xs={12} md={6}>
          <Card>
            <CardHeader>Publish Latest Version</CardHeader>
            <CardBody>
              <p>
                Publish the translations to the 'Latest' version. This will
                completely overwrite the 'Latest' version.
              </p>
              <Button
                primary
                onClick={handlePublishLatestClick}
                loading={isPublishingLatest}
              >
                Publish Latest
              </Button>
            </CardBody>
          </Card>
          <Card>
            <CardHeader>Copy Versions</CardHeader>
            <CardBody>
              <p>
                Copy from one version to another version. This allows you to
                easily promote changes from one version to another. When doing a
                copy, the target version will get completely overwritten with
                the source version. You cannot overwrite the 'Latest' version.
              </p>
              <label>Source Version</label>
              <Dropdown
                options={versions.filter(x => x.lastPublishedAt !== null)}
                getOptionLabel={version => version.name}
                getOptionValue={version => version.id}
                value={selectedSourceVersion}
                onChange={setSelectedSourceVersion}
              />
              <br />
              <label>Target Version</label>
              <Dropdown
                options={versions.filter(x => x.name !== 'Latest')}
                getOptionLabel={version => version.name}
                getOptionValue={version => version.id}
                value={selectedTargetVersion}
                onChange={setSelectedTargetVersion}
              />
              <br />
              <Button primary onClick={toggleCopyVersionModal}>
                Copy
              </Button>
            </CardBody>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <CardHeader>Versions and Builds</CardHeader>
            <CardBody>
              <p>
                Copy from one version to another version. This allows you to
                easily promote changes from one version to another. When doing a
                copy, the target version will get completely overwritten with
                the source version.
              </p>
              <Table striped borderless columns={cols} data={versions} />
            </CardBody>
          </Card>
        </Col>
      </Row>
      <AddVersionModal
        hidden={addVersionModalHidden}
        toggle={toggleVersionModal}
        onSaveClick={handleAddVersionSave}
      />
      <ConfirmationModal
        hidden={deleteVersionModalHidden}
        toggle={toggleDeleteVersionModal}
        onConfirm={deleteVersion}
        body={
          'Are you sure you want to delete this version? This action cannot be undone.'
        }
      />
      <ConfirmationModal
        hidden={copyVersionModalHidden}
        toggle={toggleCopyVersionModal}
        onConfirm={handleCopyVersion}
        body={
          'Are you sure you want to copy versions? The target version will be completely overwritten with the source version.'
        }
      />
    </div>
  );
};

export default Publish;
